/**
 * 外注見積の一覧 /vendor-quotes（#198、docs/79。試作 screen_sample/現場力_外注見積の一覧_案_v1.html）。
 *   ・左のメニュー「見積書」の下（使う機能 vendorQuote の会社だけ）。案件の画面のタブからは ?group=<案件>&via=group で開く（題の左に「‹ 案件名」）
 *   ・絞り込み（期間・{vendor}・{project}・担当者・金額の範囲・件名・番号）と状態のタブは URL の問い合わせに持つ（戻る・ブックマークで同じ形）
 *   ・表示切替（一覧／{vendor}別／{project}別）は端末に覚える（localStorage gb.vq.view）。小計行は既定で閉じた形
 *   ・行を押すと詳細の画面 /vendor-quotes/:id?<一覧の条件>（Detail.tsx。#198 の続き、docs/79 §13＝右側パネルはやめた）。
 *     戻ってきたら開いていた行に印（is-selected）を付け、スクロールの位置を戻す（sessionStorage gb.vq.back）
 *   ・スマホ（スマホ版か幅 767px 以下）はカード（取込日・{vendor}・金額・状態）・絞り込みのシート・見るだけの詳細のシート。登録と CSV は出さない（PC 専用）
 *   ・データが届く前も表・タブ・合計の段の場所を取る（docs/70）
 */
import {
  ORDER_BUNDLE_MSG,
  VENDOR_QUOTE_LIST_MSG,
  VENDOR_QUOTE_LIST_STATE_LABEL,
  VENDOR_QUOTE_LIST_TABS,
  type VendorQuoteListFilters,
  type VendorQuoteListGroup,
  type VendorQuoteListResponse,
  type VendorQuoteAdoption,
  type VendorQuoteListTab,
  type VendorQuoteRow,
} from "@genbaryoku/contract";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api, fetchFile, messageOf } from "../../api.js";
import { BackButton, usePageName } from "../../components/BackButton.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { Icon } from "../../components/Shell.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { notify } from "../../components/Notice.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { Sheet } from "../../components/Sheet.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { PASSIVE } from "../../lib/autoLogout.js";
import { usePcOnly } from "../../lib/pcOnly.js";
import { t, useLabels } from "../../labels.js";
import { PhoneDetailSheet } from "./PhoneDetail.jsx";
import { useMe } from "../../auth.js";
import { AdoptionChip, BundleBar, SelectBox, useOrderBundle } from "../orders/bundle.jsx";
import { ICON, ReadCell, StateChip, docIcon, docTitle, invalidateList, receivedLabel, receivedTitle, titleOf, yenOrDash } from "./parts.jsx";

type View = "list" | "vendor" | "group";
/** 詳細へ移る前の一覧の位置（戻ってきたら印とスクロール。sessionStorage＝そのタブだけ） */
const BACK_KEY = "gb.vq.back";
const VIEW_KEY = "gb.vq.view";
const readView = (): View => {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "vendor" || v === "group" ? v : "list";
  } catch {
    return "list";
  }
};
/** URL の問い合わせのうち、API に渡す絞り込み（state は tab） */
const FILTER_KEYS = ["period", "from", "to", "vendor", "group", "assignee", "adoption", "min", "max", "q", "sort"] as const;
const PERIODS: [string, string][] = [["all", "すべて"], ["this", "今月"], ["last", "先月"], ["3m", "過去3か月"], ["range", "期間を指定"]];
const digits = (s: string) => s.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/[^0-9]/g, "");

export function VendorQuoteListPage() {
  const L = useLabels();
  const [sp, setSp] = useSearchParams();
  const pc = usePcOnly();
  const phone = pc.phone;
  const me = useMe();
  usePageName(L.vendorQuote);
  const tab = (VENDOR_QUOTE_LIST_TABS as readonly string[]).includes(sp.get("state") ?? "") ? (sp.get("state") as VendorQuoteListTab) : "all";
  const [view, setViewRaw] = useState<View>(readView);
  const setView = (v: View) => {
    setViewRaw(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* 端末に覚えられなくても動く */
    }
  };
  /** 問い合わせを直す。元はいまの URL（react-router の関数の形は描いたときの問い合わせを渡すので、待ってから呼ぶと古い条件が戻る） */
  const setParam = (patch: Record<string, string | null>) => {
    const n = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "" || (k === "period" && v === "all") || (k === "state" && v === "all")) n.delete(k);
      else n.set(k, v);
    }
    setSp(n, { replace: true });
  };
  // 件名・番号：打つたびには引かず 0.3 秒待つ
  const [qText, setQText] = useState(sp.get("q") ?? "");
  useEffect(() => {
    const h = window.setTimeout(() => {
      if ((sp.get("q") ?? "") !== qText.trim()) setParam({ q: qText.trim() || null });
    }, 300);
    return () => window.clearTimeout(h);
  }, [qText]); // eslint-disable-line react-hooks/exhaustive-deps
  const filterQs = useMemo(() => {
    const n = new URLSearchParams();
    for (const k of FILTER_KEYS) {
      const v = sp.get(k);
      if (v) n.set(k, v);
    }
    return n.toString();
  }, [sp]);
  const anyFilter = FILTER_KEYS.some((k) => k !== "sort" && !!sp.get(k));
  const stateQs = tab === "all" ? "" : `state=${tab}`;
  const listQs = [filterQs, stateQs].filter(Boolean).join("&");

  // 定期の読み直し（AI読み取り中があるとき）は人の操作でない＝PASSIVE（自動ログアウトの時刻を進めない）
  const auto = useRef(false);
  const get = <T,>(path: string) => api<T>("GET", path, undefined, auto.current ? PASSIVE : undefined);
  const list = useInfiniteQuery({
    queryKey: ["vql", listQs],
    queryFn: ({ pageParam }) => get<VendorQuoteListResponse>(`/vendor-quotes?${listQs}${listQs ? "&" : ""}limit=50${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: view === "list",
  });
  const groupsQ = useQuery({
    queryKey: ["vql", listQs, "groups", view],
    queryFn: () => get<VendorQuoteListResponse>(`/vendor-quotes?${listQs}${listQs ? "&" : ""}view=${view}&limit=0`),
    placeholderData: keepPreviousData,
    enabled: view !== "list",
  });
  const filters = useQuery({ queryKey: ["vql-filters"], queryFn: () => api<VendorQuoteListFilters>("GET", "/vendor-quotes/filters") });
  const head = view === "list" ? list.data?.pages[0] : groupsQ.data;
  const items = useMemo(() => (list.data?.pages ?? []).flatMap((p) => p.items), [list.data]);
  const hasReading = !!head && (head.counts.reading > 0);
  useEffect(() => {
    if (!hasReading) return;
    const h = window.setInterval(() => {
      auto.current = true;
      const p = view === "list" ? list.refetch() : groupsQ.refetch();
      void p.finally(() => {
        auto.current = false;
      });
    }, 10_000);
    return () => window.clearInterval(h);
  }, [hasReading, view]); // eslint-disable-line react-hooks/exhaustive-deps

  // 詳細のシート（スマホ）。PC は行を押すと詳細の画面へ（右側パネルはやめた。docs/79 §13.2 A）
  const [sel, setSel] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const qcl = useQueryClient();
  /** 詳細から戻ったとき：開いていた行に印・スクロールの位置（同じ条件の一覧に戻ったときだけ） */
  const [lastId, setLastId] = useState<string | null>(null);
  const restore = useRef<{ y: number } | null>(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(BACK_KEY);
      if (!raw) return;
      sessionStorage.removeItem(BACK_KEY);
      const b = JSON.parse(raw) as { search: string; y: number; id: string };
      if (b.search !== window.location.search) return;
      setLastId(b.id);
      restore.current = { y: b.y };
    } catch {
      /* 覚えられない端末は戻すだけ */
    }
  }, []);
  const openDetail = (id: string) => {
    try {
      sessionStorage.setItem(BACK_KEY, JSON.stringify({ search: window.location.search, y: window.scrollY, id }));
    } catch {
      /* 覚えられない端末は位置を戻さない */
    }
    nav(`/vendor-quotes/${id}${window.location.search}`);
  };
  const open = (id: string) => (phone ? setSel(id) : openDetail(id));
  // 詳細の画面で削除して戻ってきたとき：「元に戻す」つきの通知（docs/79 §13.3）
  useEffect(() => {
    const st = loc.state as { vqDeleted?: { id: string; message: string } } | null;
    if (!st?.vqDeleted) return;
    const d = st.vqDeleted;
    nav(loc.pathname + loc.search, { replace: true, state: null });
    notify({
      text: d.message,
      action: {
        label: "元に戻す",
        onClick: () => {
          void api("POST", `/vendor-quotes/${d.id}/restore`).then(() => invalidateList(qcl), (e) => notify({ text: messageOf(e), tone: "error" }));
        },
      },
    });
  }, [loc.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows: VendorQuoteRow[] = items;
  // #233 2a：行を選ぶ（一覧の表示・PC・モジュール sales）→ 帯で 採用・不採用・まとめて作成（1件は「{order}作成」）
  const canBundle = !phone && view === "list" && !!me.data?.tenant.modules.includes("sales") && !pc.hidden("/orders/new");
  const [picked, setPicked] = useState<Map<string, VendorQuoteRow>>(new Map());
  const pick = (r: VendorQuoteRow, on: boolean) =>
    setPicked((m) => {
      const n = new Map(m);
      if (on) n.set(r.id, r);
      else n.delete(r.id);
      return n;
    });
  // 条件を変えたら選び直し（見えていない行を作らない）
  useEffect(() => setPicked(new Map()), [listQs, view]);
  const bundle = useOrderBundle();
  const pickedRows = [...picked.values()];
  const doBundle = () => {
    bundle.start({ vendorQuoteIds: pickedRows.map((r) => r.id) }, pickedRows.flatMap((r) => r.orders)).catch((e) => notify({ text: messageOf(e), tone: "error" }));
  };
  /** 選んだ回答をまとめて採用・不採用（印だけ。通知の「元に戻す」で前の印に） */
  const [adoptBusy, setAdoptBusy] = useState(false);
  const adoptPicked = async (next: VendorQuoteAdoption) => {
    setAdoptBusy(true);
    const done: Array<{ id: string; prev: VendorQuoteAdoption | null }> = [];
    try {
      for (const r of pickedRows) {
        if (r.adoption === next) continue;
        await api("PUT", `/vendor-quotes/${r.id}/adoption`, { adoption: next });
        done.push({ id: r.id, prev: r.adoption });
      }
    } catch (e) {
      notify({ text: messageOf(e), tone: "error" });
    } finally {
      setAdoptBusy(false);
      setPicked(new Map());
      invalidateList(qcl);
    }
    if (done.length) {
      notify({
        text: next === "adopted" ? ORDER_BUNDLE_MSG.adopted : ORDER_BUNDLE_MSG.declined,
        action: {
          label: ORDER_BUNDLE_MSG.undo,
          onClick: () => void Promise.all(done.map((d) => api("PUT", `/vendor-quotes/${d.id}/adoption`, { adoption: d.prev }))).then(() => invalidateList(qcl), (e) => notify({ text: messageOf(e), tone: "error" })),
        },
      });
    }
  };
  // 行が描かれたら位置を戻す（1回）
  useLayoutEffect(() => {
    if (!restore.current || !head) return;
    const y = restore.current.y;
    restore.current = null;
    window.scrollTo(0, y);
  }, [head]);

  const groupFilter = sp.get("group");
  const fromGroup = sp.get("via") === "group" && !!groupFilter && groupFilter !== "none";
  const groupName = filters.data?.groups.find((g) => g.id === groupFilter)?.name ?? L.project;
  const regTo = `/vendor-quotes/new${groupFilter && groupFilter !== "none" ? `?group=${groupFilter}` : ""}`;
  const [csvBusy, setCsvBusy] = useState(false);
  const downloadCsv = async () => {
    setCsvBusy(true);
    try {
      const blob = await fetchFile(`/api/v1/vendor-quotes/export.csv?${listQs}`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const d = new Date();
      a.download = `${L.vendorQuote}_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, 1000);
    } catch (e) {
      notify({ text: messageOf(e), tone: "error" });
    } finally {
      setCsvBusy(false);
    }
  };
  const clear = () => {
    setQText("");
    const n = new URLSearchParams();
    const st = new URLSearchParams(window.location.search).get("state");
    if (st) n.set("state", st);
    setSp(n, { replace: true });
  };

  const counts = head?.counts;
  const total = head?.total;
  const err = view === "list" ? list.error : groupsQ.error;
  const viewOptions = [
    { value: "list" as const, label: "一覧", testId: "vql-view-list" },
    { value: "vendor" as const, label: `${L.vendor}別`, testId: "vql-view-vendor" },
    { value: "group" as const, label: `${L.project}別`, testId: "vql-view-group" },
  ];
  const opt = (v: string, label: string) => <option key={v} value={v}>{label}</option>;
  const sel1 = (key: string, label: string, options: ReactNode, testId: string) => (
    <label className={"vql-fsel" + (sp.get(key) ? " is-on" : "")}>
      <span>{label}</span>
      <select value={sp.get(key) ?? ""} onChange={(e) => setParam({ [key]: e.target.value || null })} aria-label={label} data-testid={testId}>
        {options}
      </select>
    </label>
  );
  const vendorSel = sel1("vendor", L.vendor, [opt("", "すべて"), opt("none", VENDOR_QUOTE_LIST_MSG.unset), ...(filters.data?.vendors ?? []).map((v) => opt(v.id, v.name))], "vql-filter-vendor");
  const groupSel = sel1("group", L.project, [opt("", "すべて"), opt("none", VENDOR_QUOTE_LIST_MSG.unset), ...(filters.data?.groups ?? []).map((g) => opt(g.id, g.name))], "vql-filter-group");
  const adoptionSel = sel1("adoption", ORDER_BUNDLE_MSG.adoptionFilter, [opt("", ORDER_BUNDLE_MSG.adoptionAll), opt("adopted", ORDER_BUNDLE_MSG.adopt), opt("declined", ORDER_BUNDLE_MSG.decline)], "vql-filter-adoption");
  const assigneeSel = sel1("assignee", "担当者", [opt("", "すべて"), opt("me", "自分"), opt("none", VENDOR_QUOTE_LIST_MSG.unset), ...(filters.data?.users ?? []).map((u) => opt(u.id, u.name))], "vql-filter-assignee");
  const periodSel = (
    <label className={"vql-fsel" + (sp.get("period") ? " is-on" : "")}>
      <span>期間</span>
      <select value={sp.get("period") ?? "all"} onChange={(e) => setParam({ period: e.target.value, ...(e.target.value === "range" ? {} : { from: null, to: null }) })} aria-label="期間" data-testid="vql-filter-period">
        {PERIODS.map(([v, l]) => opt(v, l))}
      </select>
    </label>
  );
  const amountRange = (
    <span className={"vql-frange" + (sp.get("min") || sp.get("max") ? " is-on" : "")}>
      <span>金額</span>
      <MoneyInput value={sp.get("min") ?? ""} onCommit={(v) => setParam({ min: v })} label="金額の下限（税抜）" testId="vql-filter-min" />
      〜
      <MoneyInput value={sp.get("max") ?? ""} onCommit={(v) => setParam({ max: v })} label="金額の上限（税抜）" testId="vql-filter-max" />
      円
    </span>
  );
  const search = <FilterInput value={qText} onChange={setQText} placeholder="件名・番号で絞り込み" testId="vql-filter-q" />;
  const tabs = VENDOR_QUOTE_LIST_TABS.map((k) => ({ key: k, label: VENDOR_QUOTE_LIST_STATE_LABEL[k], count: counts ? counts[k] : "…" }));
  const nFilters = ["period", "vendor", "group", "assignee", "adoption"].filter((k) => !!sp.get(k)).length + (sp.get("min") || sp.get("max") ? 1 : 0);

  return (
    <div className="vql">
      <div className="vql-list">
        <ListHead
          back={fromGroup ? <BackButton fallback={{ to: `/groups/${groupFilter}`, label: groupName }} /> : null}
          title={L.vendorQuote}
          extra={
            <>
              <span className="muted num" data-testid="vql-count">{counts ? `${counts.all}件` : ""}</span>
              {phone ? null : (
                <>
                  <span className="sp" />
                  <Segmented options={viewOptions} value={view} onChange={setView} label="表示" testId="vql-view" className="vql-seg" />
                  <button type="button" className="btn text" disabled={csvBusy} onClick={() => void downloadCsv()} data-testid="vql-csv">{VENDOR_QUOTE_LIST_MSG.csv}</button>
                </>
              )}
            </>
          }
        >
        {phone ? (
          <div className="vql-phtools">
            {search}
            <button type="button" className="btn" onClick={() => setFilterOpen(true)} data-testid="vql-filter-open">
              {ICON.filter}絞り込み{nFilters ? <span className="vql-badge">{nFilters}</span> : null}
            </button>
          </div>
        ) : (
          <div className="vql-filters" data-testid="vql-filters">
            {periodSel}
            {sp.get("period") === "range" ? (
              <span className="vql-fdates">
                <DateInput value={sp.get("from") ?? ""} onChange={(v) => setParam({ from: v || null })} label="開始" ariaLabel="期間の開始" testId="vql-filter-from" />
                〜
                <DateInput value={sp.get("to") ?? ""} onChange={(v) => setParam({ to: v || null })} label="終了" ariaLabel="期間の終了" testId="vql-filter-to" />
              </span>
            ) : null}
            {vendorSel}
            {groupSel}
            {assigneeSel}
            {adoptionSel}
            {amountRange}
            {search}
            <button type="button" className="btn text" hidden={!anyFilter && !qText} onClick={clear} data-testid="vql-clear">{VENDOR_QUOTE_LIST_MSG.clear}</button>
          </div>
        )}
        {err ? <ErrorText>{messageOf(err)}</ErrorText> : null}
        {canBundle ? (
          <BundleBar count={picked.size} busy={bundle.busy || adoptBusy} onCreate={doBundle} onClear={() => setPicked(new Map())}>
            <button type="button" className="btn" disabled={adoptBusy} onClick={() => void adoptPicked("adopted")} data-testid="bundle-adopt">{ORDER_BUNDLE_MSG.adopt}</button>
            <button type="button" className="btn" disabled={adoptBusy} onClick={() => void adoptPicked("declined")} data-testid="bundle-decline">{ORDER_BUNDLE_MSG.decline}</button>
          </BundleBar>
        ) : null}
        {/* #241：タブの段は表のカードの中の上辺・右端に「＋ {vendorQuote}登録」。表示（一覧・{vendor}別・{project}別）と CSV は題の行の右。スマホはカードの並び（今のまま） */}
        <div className={phone ? undefined : "lcard vql-lcard"}>
          {phone ? (
            <div className="vql-tabsrow">
              <ListTabs id="vql" label={`${L.vendorQuote}を状態で絞る`} tabs={tabs} value={tab} onChange={(k) => setParam({ state: k })} />
              <Segmented options={viewOptions} value={view} onChange={setView} label="表示" testId="vql-view" className="vql-seg" />
            </div>
          ) : (
            <ListTabs
              id="vql"
              label={`${L.vendorQuote}を状態で絞る`}
              tabs={tabs}
              value={tab}
              onChange={(k) => setParam({ state: k })}
              create={
                phone || pc.hidden(regTo) ? undefined : (
                  <Link className="btn primary" to={regTo} data-testid="vql-register">
                    <Icon k="plus" />
                    {t(VENDOR_QUOTE_LIST_MSG.register)}
                  </Link>
                )
              }
            />
          )}
        {phone ? (
          <div className="vql-cards" {...listTabPanel("vql", tab)} data-testid="vql-cards">
            {view === "list" ? (
              rows.map((r) => <Card key={r.id} r={r} onOpen={() => open(r.id)} />)
            ) : (
              <Groups groups={groupsQ.data?.groups ?? null} view={view} listQs={listQs} cards onOpen={open} sel={sel} />
            )}
            {head && head.total.count === 0 ? <p className="vql-empty">{t(anyFilter || tab !== "all" ? VENDOR_QUOTE_LIST_MSG.empty : VENDOR_QUOTE_LIST_MSG.emptyAll)}</p> : null}
            {view === "list" && list.hasNextPage ? <button type="button" className="btn text vql-more" onClick={() => void list.fetchNextPage()}>{VENDOR_QUOTE_LIST_MSG.more}</button> : null}
          </div>
        ) : (
          <div className="vql-card" {...listTabPanel("vql", tab)}>
            <table className="vql-tbl" data-testid="vql-table" aria-busy={!head}>
              <colgroup>
                <col className={"c-date" + (canBundle ? " is-sel" : "")} />
                <col className="c-vendor" />
                <col className="c-gt c-opt" />
                <col className="c-amt" />
                <col className="c-read c-opt" />
                <col className="c-state" />
                <col className="c-use c-opt" />
                <col className="c-doc c-opt" />
                <col className="c-who c-opt" />
              </colgroup>
              <thead>
                <tr>
                  <th><SortBtn k="received" label="取込日" sp={sp} setParam={setParam} /></th>
                  <th>{L.vendor}</th>
                  <th className="c-opt">{L.project}・{L.subject}</th>
                  <th className="r"><SortBtn k="amount" label="金額（税抜）" sp={sp} setParam={setParam} /></th>
                  <th className="c-opt">読み取り</th>
                  <th>状態</th>
                  <th className="c-opt">反映</th>
                  <th className="c-opt">資料</th>
                  <th className="c-opt">担当者</th>
                </tr>
              </thead>
              <tbody>
                {view === "list" ? (
                  rows.map((r) => <Row key={r.id} r={r} sel={lastId === r.id} onOpen={() => open(r.id)} pick={canBundle ? { on: picked.has(r.id), set: (on) => pick(r, on) } : undefined} />)
                ) : (
                  <Groups groups={groupsQ.data?.groups ?? null} view={view} listQs={listQs} onOpen={open} sel={lastId} />
                )}
                {head && head.total.count === 0 ? (
                  <tr className="emptyrows">
                    <td colSpan={9}>
                      {t(anyFilter || tab !== "all" ? VENDOR_QUOTE_LIST_MSG.empty : VENDOR_QUOTE_LIST_MSG.emptyAll)}
                      {anyFilter ? <button type="button" className="btn text" onClick={clear}>{VENDOR_QUOTE_LIST_MSG.clear}</button> : null}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            {view === "list" && list.hasNextPage ? (
              <div className="vql-more">
                <button type="button" className="btn text" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()} data-testid="vql-more">{VENDOR_QUOTE_LIST_MSG.more}</button>
              </div>
            ) : null}
          </div>
        )}
        </div>
        {bundle.dialog}
        {/* 下の段：絞り込んだ範囲の件数と税抜の合計（金額なしの件数）。届く前も場所を取る */}
        <div className="vql-sum" data-notice-above data-testid="vql-sum">
          <span className="num">{total ? `${total.count}件` : "…"}</span>
          <span>{VENDOR_QUOTE_LIST_MSG.sum}<b className="num">{total ? total.exTax.toLocaleString("ja-JP") : "…"}</b>円</span>
          {total && total.noAmount ? <span className="muted">{VENDOR_QUOTE_LIST_MSG.noAmount.replace("{n}", String(total.noAmount))}</span> : null}
        </div>
        </ListHead>
      </div>
      {phone ? (
        <>
          <Sheet open={filterOpen} title="絞り込み" cancelLabel="取消" doneLabel="決定" onCancel={() => setFilterOpen(false)} onDone={() => setFilterOpen(false)} testId="vql-filter-sheet">
            <div className="vql-phfilters">
              {periodSel}
              {vendorSel}
              {groupSel}
              {assigneeSel}
              {adoptionSel}
              {amountRange}
              <button type="button" className="btn text" onClick={clear}>{VENDOR_QUOTE_LIST_MSG.clear}</button>
            </div>
          </Sheet>
          <PhoneDetailSheet id={sel} onClose={() => setSel(null)} />
        </>
      ) : null}
    </div>
  );
}

/** 金額の欄（数だけ。桁区切りは欄を離れたとき＝docs/10 §5.2） */
function MoneyInput({ value, onCommit, label, testId }: { value: string; onCommit: (v: string | null) => void; label: string; testId: string }) {
  const [v, setV] = useState(value ? Number(value).toLocaleString("ja-JP") : "");
  useEffect(() => setV(value ? Number(value).toLocaleString("ja-JP") : ""), [value]);
  const commit = () => {
    const d = digits(v);
    setV(d ? Number(d).toLocaleString("ja-JP") : "");
    if (d !== value) onCommit(d || null);
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && !e.nativeEvent.isComposing && commit()}
      aria-label={label}
      data-testid={testId}
    />
  );
}

function SortBtn({ k, label, sp, setParam }: { k: "received" | "amount"; label: string; sp: URLSearchParams; setParam: (p: Record<string, string | null>) => void }) {
  const cur = sp.get("sort") ?? "received_desc";
  const on = cur.startsWith(k);
  const desc = cur.endsWith("desc");
  return (
    <button
      type="button"
      className={"vql-sort" + (on ? " is-on" : "")}
      aria-label={`${label}で並べ替え`}
      onClick={() => setParam({ sort: on ? `${k}_${desc ? "asc" : "desc"}` : `${k}_desc` === "received_desc" ? null : `${k}_desc` })}
      data-testid={`vql-sort-${k}`}
    >
      {label}
      {on ? <span aria-hidden="true">{desc ? " ▼" : " ▲"}</span> : null}
    </button>
  );
}

/** 一覧の1行（PC） */
function Row({ r, sel, onOpen, inGroup, pick }: { r: VendorQuoteRow; sel: boolean; onOpen: () => void; inGroup?: "vendor" | "group"; pick?: { on: boolean; set: (on: boolean) => void } | undefined }) {
  return (
    <tr
      className={"vql-row" + (sel ? " is-selected" : "") + (inGroup ? " is-ingroup" : "")}
      tabIndex={0}
      aria-selected={sel}
      data-vql-row={r.id}
      data-testid="vql-row"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a,button,input")) return;
        onOpen();
      }}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {/* #233 2a：行を選ぶ □ は取込日の欄の左（列を足さない＝行を押すと開くのは今のまま） */}
      <td className="num" title={receivedTitle(r.receivedAt)}>
        {pick ? <SelectBox on={pick.on} label={`${r.vendor?.name ?? ""} ${titleOf(r)}`.trim()} disabled={!r.vendor || r.state === "reading"} onChange={pick.set} /> : null}
        {receivedLabel(r.receivedAt)}
      </td>
      <td>
        {inGroup === "vendor" ? null : r.vendor ? (
          <span className="vql-t1">{r.vendor.name}</span>
        ) : (
          <>
            <span className="vql-t1 vql-unset">{VENDOR_QUOTE_LIST_MSG.unset}</span>
            {r.issuerName ? <span className="vql-t2">{VENDOR_QUOTE_LIST_MSG.issuer.replace("{name}", r.issuerName.replace(/^(株式会社|有限会社)\s*/, ""))}</span> : null}
          </>
        )}
      </td>
      <td className="c-opt">
        {inGroup === "group" ? null : r.group ? <span className="vql-t1">{r.group.name}</span> : <span className="vql-t1 vql-unset">{VENDOR_QUOTE_LIST_MSG.unset}</span>}
        <span className="vql-t2">
          <span className="vql-tt">{titleOf(r)}</span>
          {r.rfq ? <Link className="vql-rfqtag" to={`/rfqs/${r.rfq.id}`} data-testid="vql-rfqtag">依頼 {r.rfq.displayNo}</Link> : null}
        </span>
      </td>
      <td className="r num vql-amt">{yenOrDash(r.totalExTax)}</td>
      <td className="c-opt"><ReadCell r={r} /></td>
      <td>
        <span className="vql-states">
          <StateChip r={r} />
          {/* #233 2a：採用のしるし（不採用は状態のチップ「不採用」） */}
          {r.adoption === "adopted" ? <AdoptionChip value="adopted" /> : null}
        </span>
      </td>
      <td className="c-opt">
        {r.usage.all ? (
          r.usage.documents.length ? (
            <Link className="vql-use" to={`/estimates/${r.usage.documents[0]!.id}`} title={r.usage.documents.map((d) => `${d.displayNo ?? "（下書き）"} ${d.subject}`).join("・")} data-testid="vql-use">{r.usage.done}／{r.usage.all}行</Link>
          ) : (
            <span className="vql-use is-zero num">{r.usage.done}／{r.usage.all}行</span>
          )
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td className="c-opt">
        <button type="button" className="vql-doc" aria-label={docTitle(r)} title={docTitle(r)} onClick={onOpen} data-testid="vql-doc">{docIcon(r.source)}</button>
      </td>
      <td className="c-opt">{r.assignee?.name ?? <span className="muted">—</span>}</td>
    </tr>
  );
}

/** 外注先別・案件別：小計行（既定は閉じた形）と、開いたグループの明細 */
function Groups({ groups, view, listQs, onOpen, sel, cards = false }: { groups: VendorQuoteListGroup[] | null; view: "vendor" | "group"; listQs: string; onOpen: (id: string) => void; sel: string | null; cards?: boolean }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  useEffect(() => setOpen(new Set()), [view]);
  if (!groups) return null;
  const allOpen = groups.length > 0 && groups.every((g) => open.has(g.key));
  const toggleAll = (
    <button type="button" className="btn text" onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map((g) => g.key)))} data-testid="vql-toggle-all">
      {allOpen ? VENDOR_QUOTE_LIST_MSG.closeAll : VENDOR_QUOTE_LIST_MSG.openAll}
    </button>
  );
  const items = groups.map((g) => {
    const on = open.has(g.key);
    const name = g.key === "none" ? VENDOR_QUOTE_LIST_MSG.unset : g.name;
    const toggle = () => setOpen((s) => {
      const n = new Set(s);
      if (n.has(g.key)) n.delete(g.key);
      else n.add(g.key);
      return n;
    });
    const btn = (
      <button type="button" className="vql-grpbtn" aria-expanded={on} onClick={toggle} data-testid="vql-group">
        <span className="vql-chev">{ICON.chev}</span>
        <span className={"vql-gn" + (g.key === "none" ? " vql-unset" : "")}>{name}</span>
        <span className="muted">{g.count}件</span>
        {cards ? null : (
          <span className="vql-gs">
            {(["review", "reading", "unused", "used", "rejected"] as const).filter((k) => g.counts[k]).map((k) => (
              <span key={k} className={`vql-chip is-${k} is-small`}>{VENDOR_QUOTE_LIST_STATE_LABEL[k]} {g.counts[k]}</span>
            ))}
          </span>
        )}
        <b className="num vql-gt">{g.totalExTax.toLocaleString("ja-JP")}円</b>
      </button>
    );
    return { g, on, btn };
  });
  if (cards) {
    return (
      <>
        <div className="vql-gtools">{toggleAll}</div>
        {items.map(({ g, on, btn }) => (
          <div key={g.key} className="vql-cgroup">
            {btn}
            {on ? <GroupItems view={view} k={g.key} listQs={listQs} onOpen={onOpen} sel={sel} cards /> : null}
          </div>
        ))}
      </>
    );
  }
  return (
    <>
      <tr className="vql-gtools-row">
        <td colSpan={9}>{toggleAll}</td>
      </tr>
      {items.map(({ g, on, btn }) => [
        <tr key={g.key} className="vql-grp">
          <td colSpan={9}>{btn}</td>
        </tr>,
        on ? <GroupItems key={`${g.key}-items`} view={view} k={g.key} listQs={listQs} onOpen={onOpen} sel={sel} /> : null,
      ])}
    </>
  );
}

function GroupItems({ view, k, listQs, onOpen, sel, cards = false }: { view: "vendor" | "group"; k: string; listQs: string; onOpen: (id: string) => void; sel: string | null; cards?: boolean }) {
  const qs = new URLSearchParams(listQs);
  qs.set(view, k);
  qs.set("limit", "200");
  const q = useQuery({ queryKey: ["vql", listQs, "group-items", view, k], queryFn: () => api<VendorQuoteListResponse>("GET", `/vendor-quotes?${qs.toString()}`) });
  const rows = q.data?.items ?? [];
  if (cards) return <>{rows.map((r) => <Card key={r.id} r={r} onOpen={() => onOpen(r.id)} />)}</>;
  if (!q.data) {
    return (
      <tr className="vql-row is-loading">
        <td colSpan={9}>…</td>
      </tr>
    );
  }
  return <>{rows.map((r) => <Row key={r.id} r={r} sel={sel === r.id} onOpen={() => onOpen(r.id)} inGroup={view} />)}</>;
}

/** スマホのカード：取込日・{vendor}・金額・状態の4つ（docs/79 決定・§3.8） */
function Card({ r, onOpen }: { r: VendorQuoteRow; onOpen: () => void }) {
  return (
    <button type="button" className="vql-vcard" onClick={onOpen} data-testid="vql-card">
      <span className="vql-r1">
        <b className={r.vendor ? undefined : "vql-unset"}>{r.vendor?.name ?? VENDOR_QUOTE_LIST_MSG.unset}</b>
        <StateChip r={r} sub={false} />
        {r.adoption === "adopted" ? <AdoptionChip value="adopted" /> : null}
      </span>
      <span className="vql-r2">
        <span className="num">{receivedLabel(r.receivedAt)}</span>
        <span className="num vql-camt">{r.totalExTax == null ? "—" : `${r.totalExTax.toLocaleString("ja-JP")}円`}</span>
      </span>
    </button>
  );
}

/** スマホの詳細（見るだけ。紐づけ・不採用・削除は PC。docs/79 §2 ヨ） */
